<template>
  <nav v-if="me" class="mtb" aria-label="Основная навигация">
    <button
      type="button"
      class="mtb__item"
      :class="{ 'mtb__item--active': currentTab === 'mine' }"
      @click="goTab('mine')"
    >
      <TNIcon name="list" :size="22" />
      <span class="mtb__label">Мои</span>
    </button>

    <button
      type="button"
      class="mtb__item"
      :class="{ 'mtb__item--active': currentTab === 'available' }"
      @click="goTab('available')"
    >
      <TNIcon name="people" :size="22" />
      <span class="mtb__label">Доступные</span>
    </button>

    <button
      v-if="!isMiniApp"
      type="button"
      class="mtb__item"
      :class="{ 'mtb__item--active': profileOpen }"
      aria-label="Профиль"
      @click="profileOpen = true"
    >
      <TNIcon name="user" :size="22" />
      <span class="mtb__label">Профиль</span>
    </button>
  </nav>

  <!-- Профиль в bottom-sheet: правый-верх экрана оставляем иконкам супераппа -->
  <!-- teleport в body: дефолтный 'main' у нас — скролл-контейнер, шит уезжает за вьюпорт -->
  <TNBottomSheet
    v-if="!isMiniApp"
    :is-open="profileOpen"
    :header="{ title: 'Профиль' }"
    teleport-to="body"
    @hide="profileOpen = false"
  >
    <div v-if="me" class="mtb__profile">
      <div class="mtb__profile-head">
        <TNUserPicture :text="initials" size="m" :square="true" class="mtb__profile-avatar" />
        <div class="mtb__profile-info">
          <span class="mtb__profile-name">{{ me.fullName }}</span>
          <span v-if="me.department" class="mtb__profile-dep">{{ me.department }}</span>
          <span v-if="me.email" class="mtb__profile-email">{{ me.email }}</span>
        </div>
      </div>
      <TNButton secondary block icon="backto" @click="handleLogout">Выйти</TNButton>
    </div>
  </TNBottomSheet>
</template>

<script lang="ts" setup>
/**
 * Мобильный нижний тап-бар (паттерн мини-аппов TN Life, ср. fura/TheTabbar).
 * Виден только на ≤768px, на верхнеуровневом экране списка форм; работает
 * и в standalone, и в iframe/WebView мини-аппа (в отличие от topbar).
 */
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import LifeBridge from '@tnlife/tn-bridge';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const me = computed(() => authStore.me);

// В мини-аппе TN Life профиль и выход предоставляет суперапп —
// свой пункт «Профиль» показываем только в standalone.
const isMiniApp = LifeBridge.isIFrame || LifeBridge.isWebView;

const currentTab = computed<'mine' | 'available'>(() =>
  route.query.tab === 'available' ? 'available' : 'mine'
);

function goTab(tab: 'mine' | 'available'): void {
  router.replace({
    name: 'forms',
    query: tab === 'available' ? { tab } : {},
  });
}

const profileOpen = ref(false);

const initials = computed(() => {
  const n = me.value?.fullName || '';
  return n
    .split(/[\s\u00b7]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
});

async function handleLogout(): Promise<void> {
  profileOpen.value = false;
  await authStore.logout();
  router.push({ name: 'forms' });
}
</script>

<style scoped>
.mtb {
  display: none; /* включается на мобиле media-запросом ниже */
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30; /* ниже bottom-sheet кита (31), чтобы шит профиля накрывал бар */
  background: var(--background-primary-a-enabled);
  border-top: 1px solid var(--border-secondary-enabled);
  box-shadow: 0 -2px 14px rgba(15, 25, 50, 0.08);
  padding: 6px 4px;
  padding-bottom: calc(6px + env(safe-area-inset-bottom, 0px));
  justify-content: space-around;
  gap: 2px;
}

.mtb__item {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 6px 2px 4px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: var(--content-secondary-enabled);
  font-family: inherit;
  font-size: var(--font-size-label-small);
  font-weight: var(--font-weight-accent);
  cursor: pointer;
  min-width: 0;
}
.mtb__item:active {
  background: var(--background-secondary-enabled);
}
.mtb__item--active {
  color: var(--content-accent-enabled);
}
.mtb__item:disabled {
  opacity: 0.6;
}

.mtb__label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

@media (max-width: 768px) {
  .mtb {
    display: flex;
  }
}

/* Профиль в bottom-sheet */
.mtb__profile {
  display: grid;
  gap: 16px;
  padding: 4px 0 8px;
}
.mtb__profile-head {
  display: flex;
  align-items: center;
  gap: 12px;
}
.mtb__profile-avatar {
  flex-shrink: 0;
  border-radius: 10px !important;
  width: 44px !important;
  height: 44px !important;
}
.mtb__profile-avatar :deep(.tn-user-picture__container) {
  border-radius: 10px !important;
}
.mtb__profile-info {
  display: grid;
  gap: 2px;
  min-width: 0;
}
.mtb__profile-name {
  font-size: var(--font-size-body-medium);
  font-weight: var(--font-weight-accent);
  color: var(--content-primary-a-enabled);
}
.mtb__profile-dep,
.mtb__profile-email {
  font-size: var(--font-size-label-medium);
  color: var(--content-secondary-enabled);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
